import { SIMPLE_NATIONAL, MEI } from '@/data/fiscal-tax-regimes';

/**
 * Lê o XML de uma NFS-e já emitida e extrai os dados de configuração do
 * prestador (códigos do serviço, alíquotas, regime tributário), para
 * pré-preencher a tela de configuração da nota fiscal.
 *
 * Suporta os dois leiautes que os nossos clientes conseguem baixar:
 *  - NACIONAL: `<NFSe><infNFSe>` + `<DPS><infDPS>` (portal do Emissor Nacional);
 *  - ABRASF 2.04: `<InfNfse><InfDeclaracaoPrestacaoServico>` (IPM/AtendeNet).
 *
 * A extração espelha os parsers do backend (`nfse-nacional/DanfseParser` e
 * `fiscal-document/providers/AbrasfDanfseParser`) — os lookups são sempre
 * escopados ao nó pai, porque várias tags se repetem no documento (CNPJ,
 * Numero, Aliquota aparecem no prestador e no tomador / na nota e no RPS).
 *
 * NÃO importa série nem sequencial: o cliente que emite manual em paralelo
 * precisa de numeração própria aqui, sob pena de colidir com a do portal.
 */

export const NATIONAL_LAYOUT = 'national';
export const ABRASF_LAYOUT = 'abrasf';

const ABRASF_TO_NATIONAL_SPECIAL_TAX_REGIME = {
  1: '3',
  2: '2',
  3: '6',
  4: '1',
};

function el(parent, tag) {
  if (!parent || !parent.getElementsByTagName) return null;

  const direct = parent.getElementsByTagName(tag)[0];
  if (direct) return direct;

  // Alguns portais exportam o XML com prefixo de namespace (`<ns2:infDPS>`) e o
  // getElementsByTagName casa pelo nome qualificado — daí o fallback no localName.
  const all = parent.getElementsByTagName('*');
  for (let i = 0; i < all.length; i += 1) {
    if (all[i].localName === tag) return all[i];
  }
  return null;
}

function text(parent, tag) {
  const node = el(parent, tag);
  const value = node && node.textContent ? node.textContent.trim() : '';
  return value || null;
}

function digits(value) {
  return value ? String(value).replace(/\D/g, '') : '';
}

/**
 * Aceita "2.00" (ABRASF/nacional) e "1.234,56" (exportações em pt-BR). Devolve
 * `null` quando a tag não veio ou o conteúdo não é numérico — o que não dá para
 * converter fica de fora, e o campo da tela permanece como está.
 */
function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const raw = String(value).trim();
  const normalized = raw.includes(',')
    ? raw.replace(/\./g, '').replace(',', '.')
    : raw;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

/**
 * O XML traz PIS/COFINS/IR/CSLL/INSS como VALOR, não como alíquota — a tela
 * pede percentual. Derivamos pelo valor do serviço; é aproximação sujeita a
 * arredondamento, por isso quem chama avisa o usuário.
 *
 * Sem a tag do imposto ou sem valor de serviço não há como derivar: devolve
 * `null` e o campo não é tocado.
 */
function rateFromAmount(amount, serviceValue) {
  const value = toNumber(amount);
  const base = toNumber(serviceValue);
  if (value === null || !base || base <= 0 || value < 0) return null;
  return round2((value / base) * 100);
}

/**
 * Alíquota de ISS: o nacional traz percentual ("2.00"); no ABRASF há município
 * que exporta fração ("0.02"). Converte a fração e sinaliza a interpretação.
 */
function parseIssRate(value, warnings) {
  const rate = toNumber(value);
  if (rate === null) return null;

  if (rate > 0 && rate < 1) {
    warnings.push(
      `Alíquota de ISS veio como fração no XML (${rate}) — interpretada como `
      + `${round2(rate * 100)}%. Confira antes de salvar.`,
    );
    return round2(rate * 100);
  }
  return rate;
}

/**
 * Os dois leiautes repartem o IBS entre UF e município e zeram o lado que não
 * se aplica (serviço no nacional vai no município; no ABRASF de Araucária vai
 * na UF). Fica com o primeiro valor não-zero; se todos vierem zerados, com o
 * zero mesmo; se nenhum veio, `null`.
 */
function firstNumber(...values) {
  const numbers = values.map(toNumber).filter(value => value !== null);
  if (!numbers.length) return null;

  const nonZero = numbers.find(value => value !== 0);
  return nonZero === undefined ? numbers[0] : nonZero;
}

function blankData() {
  return {
    companyName: null,
    name: null,
    identity: null,
    email: null,
    phone: null,
    municipalRegistration: null,
    taxRegime: null,
    specialTaxRegime: null,
    taxCalculationRegimeSN: null,
    culturalIncentive: null,
    address: {
      postalCode: null,
      street: null,
      number: null,
      complement: null,
      neighborhood: null,
    },
    service: {
      nationalTaxationCode: null,
      municipalCode: null,
      nbsCode: null,
      cnae: null,
      description: null,
      issRate: null,
      irRate: null,
      pisRate: null,
      cofinsRate: null,
      csllRate: null,
      inssRate: null,
      cbsRate: null,
      ibsRate: null,
      ibsCbsCst: null,
      ibsCbsClassTrib: null,
      ibsCbsReduction: null,
    },
  };
}

/**
 * Leiaute nacional: prestador em `infNFSe>emit`, configuração fiscal em
 * `infDPS` (`prest>regTrib`, `serv>cServ`, `valores>trib`).
 */
function parseNational(doc, warnings) {
  const data = blankData();

  const infNFSe = el(doc, 'infNFSe');
  const infDPS = el(doc, 'infDPS');
  const emit = el(infNFSe, 'emit');
  const prest = el(infDPS, 'prest');
  const regTrib = el(prest, 'regTrib');
  const cServ = el(el(infDPS, 'serv'), 'cServ');
  const valores = el(infDPS, 'valores');
  const tribMun = el(valores, 'tribMun');
  const tribFed = el(valores, 'tribFed');
  const serviceValue = text(el(valores, 'vServPrest'), 'vServ');

  data.identity = text(emit, 'CNPJ') || text(prest, 'CNPJ') || null;
  data.companyName = text(emit, 'xNome');
  data.name = text(emit, 'xFant');
  data.email = text(emit, 'email');
  data.phone = text(emit, 'fone');

  const enderNac = el(emit, 'enderNac');
  data.address.postalCode = digits(text(enderNac, 'CEP')) || null;
  data.address.street = text(enderNac, 'xLgr');
  data.address.number = text(enderNac, 'nro');
  data.address.complement = text(enderNac, 'xCpl');
  data.address.neighborhood = text(enderNac, 'xBairro');

  // A IM é omitida na maioria das notas nacionais (o município já a mantém no
  // CNC), então normalmente não há o que importar.
  data.municipalRegistration = digits(text(prest, 'IM')) || null;
  if (!data.municipalRegistration) {
    warnings.push('A Inscrição Municipal não consta no XML — preencha manualmente.');
  }

  // opSimpNac: 1 = Não optante, 2 = Optante MEI, 3 = Optante ME/EPP.
  const opSimpNac = text(regTrib, 'opSimpNac');
  if (opSimpNac === '2') data.taxRegime = MEI;
  else if (opSimpNac === '3') data.taxRegime = SIMPLE_NATIONAL;
  else if (opSimpNac === '1') {
    warnings.push(
      'O XML indica prestador não optante pelo Simples, mas não diz se é Lucro '
      + 'Presumido ou Real — selecione o regime tributário.',
    );
  }

  const regEspTrib = text(regTrib, 'regEspTrib');
  if (regEspTrib && regEspTrib !== '0') data.specialTaxRegime = regEspTrib;
  data.taxCalculationRegimeSN = text(regTrib, 'regApTribSN');

  data.service.nationalTaxationCode = text(cServ, 'cTribNac');
  data.service.municipalCode = text(cServ, 'cTribMun');
  data.service.nbsCode = text(cServ, 'cNBS');
  data.service.description = text(cServ, 'xDescServ');

  // pAliq não é enviado em nota de Simples com ISS não retido (o ISS sai no DAS).
  data.service.issRate = parseIssRate(text(tribMun, 'pAliq'), warnings);

  const piscofins = el(tribFed, 'piscofins');
  data.service.pisRate = rateFromAmount(text(piscofins, 'vPis'), serviceValue);
  data.service.cofinsRate = rateFromAmount(text(piscofins, 'vCofins'), serviceValue);
  data.service.irRate = rateFromAmount(text(tribFed, 'vRetIRRF'), serviceValue);
  data.service.csllRate = rateFromAmount(text(tribFed, 'vRetCSLL'), serviceValue);
  data.service.inssRate = rateFromAmount(text(tribFed, 'vRetCP'), serviceValue);

  const ibsCbs = el(valores, 'IBSCBS');
  if (ibsCbs) {
    data.service.ibsCbsCst = text(ibsCbs, 'CST');
    data.service.ibsCbsClassTrib = text(ibsCbs, 'cClassTrib');
    // Serviço: o IBS fica todo na competência municipal (o grupo da UF vai zerado).
    data.service.ibsRate = firstNumber(
      text(el(ibsCbs, 'gIBSMun'), 'pIBSMun'),
      text(el(ibsCbs, 'gIBSUF'), 'pIBSUF'),
    );
    data.service.cbsRate = toNumber(text(el(ibsCbs, 'gCBS'), 'pCBS'));
  }

  return data;
}

/**
 * Leiaute ABRASF 2.04 (IPM/AtendeNet). Aceita tanto o XML autorizado
 * (`InfNfse`) quanto o da declaração isolada (`InfDeclaracaoPrestacaoServico`).
 */
function parseAbrasf(doc, warnings) {
  const data = blankData();

  const infNfse = el(doc, 'InfNfse');
  const decl = el(doc, 'InfDeclaracaoPrestacaoServico');
  const prestadorServico = el(infNfse, 'PrestadorServico');
  const prestador = el(decl, 'Prestador');
  const servico = el(decl, 'Servico');
  const valores = el(servico, 'Valores');
  const serviceValue = text(valores, 'ValorServicos');

  data.identity = text(prestador, 'Cnpj') || text(prestador, 'Cpf') || null;
  data.companyName = text(prestadorServico, 'RazaoSocial');
  data.name = text(prestadorServico, 'NomeFantasia');
  data.municipalRegistration = text(prestador, 'InscricaoMunicipal');

  const contato = el(prestadorServico, 'Contato');
  data.email = text(contato, 'Email');
  data.phone = text(contato, 'Telefone');

  // O bloco de endereço do ABRASF é um <Endereco> que embrulha outro <Endereco>
  // (o logradouro) — por isso o logradouro é lido de dentro do nó pai.
  const endereco = el(prestadorServico, 'Endereco');
  data.address.postalCode = digits(text(endereco, 'Cep')) || null;
  data.address.street = text(endereco, 'Endereco');
  data.address.number = text(endereco, 'Numero');
  data.address.complement = text(endereco, 'Complemento');
  data.address.neighborhood = text(endereco, 'Bairro');

  if (text(decl, 'OptanteSimplesNacional') === '1') {
    data.taxRegime = SIMPLE_NATIONAL;
  } else if (text(decl, 'OptanteSimplesNacional') === '2') {
    warnings.push(
      'O XML indica prestador não optante pelo Simples, mas não diz se é Lucro '
      + 'Presumido ou Real — selecione o regime tributário.',
    );
  }

  const regimeEspecial = text(decl, 'RegimeEspecialTributacao');
  if (ABRASF_TO_NATIONAL_SPECIAL_TAX_REGIME[regimeEspecial]) {
    data.specialTaxRegime = ABRASF_TO_NATIONAL_SPECIAL_TAX_REGIME[regimeEspecial];
  }

  // IncentivoFiscal: 1 = Sim, 2 = Não. Sem a tag, o campo da tela não é tocado.
  const incentivoFiscal = text(decl, 'IncentivoFiscal');
  if (incentivoFiscal) data.culturalIncentive = incentivoFiscal === '1';

  // No IPM o item da lista de serviço é o código que guardamos como "Cód.
  // Tributação Nacional"; mantemos a pontuação como veio ("04.03.01").
  data.service.nationalTaxationCode = text(servico, 'ItemListaServico');
  data.service.municipalCode = text(servico, 'CodigoTributacaoMunicipio');
  data.service.nbsCode = text(servico, 'CodigoNbs');
  data.service.cnae = text(servico, 'CodigoCnae');
  data.service.description = text(servico, 'Discriminacao');

  data.service.issRate = parseIssRate(
    text(valores, 'Aliquota') || text(el(infNfse, 'ValoresNfse'), 'Aliquota'),
    warnings,
  );
  data.service.pisRate = rateFromAmount(text(valores, 'ValorPis'), serviceValue);
  data.service.cofinsRate = rateFromAmount(text(valores, 'ValorCofins'), serviceValue);
  data.service.irRate = rateFromAmount(text(valores, 'ValorIr'), serviceValue);
  data.service.csllRate = rateFromAmount(text(valores, 'ValorCsll'), serviceValue);
  data.service.inssRate = rateFromAmount(text(valores, 'ValorInss'), serviceValue);

  // Reforma: o CST/classificação ficam no bloco declarado (gIBSCBS) e as
  // alíquotas no bloco de valores dentro de <Servico>.
  const gIbsCbs = el(doc, 'gIBSCBS');
  data.service.ibsCbsCst = text(gIbsCbs, 'CST');
  data.service.ibsCbsClassTrib = text(gIbsCbs, 'cClassTrib');

  const ibsCbsValores = el(el(servico, 'IBSCBS'), 'valores');
  if (ibsCbsValores) {
    data.service.ibsRate = firstNumber(
      text(el(ibsCbsValores, 'uf'), 'pIBSUF'),
      text(el(ibsCbsValores, 'mun'), 'pIBSMun'),
    );
    data.service.cbsRate = toNumber(text(el(ibsCbsValores, 'fed'), 'pCBS'));
    data.service.ibsCbsReduction = firstNumber(
      text(el(ibsCbsValores, 'uf'), 'pRedAliqUF'),
      text(el(ibsCbsValores, 'fed'), 'pRedAliqCBS'),
    );
  }

  return data;
}

/**
 * Interpreta o XML de uma NFS-e emitida e devolve os campos da configuração.
 *
 * @param {string} xml conteúdo do arquivo
 * @returns {{ layout: string, data: Object, warnings: string[] }}
 * @throws {Error} quando o arquivo não é um XML de NFS-e reconhecido
 */
export function parseFiscalSettingsXml(xml) {
  const doc = new DOMParser().parseFromString(xml || '', 'text/xml');

  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('O arquivo não é um XML válido.');
  }

  const warnings = [];
  let layout = null;
  let data = null;

  if (el(doc, 'infDPS')) {
    layout = NATIONAL_LAYOUT;
    data = parseNational(doc, warnings);
  } else if (el(doc, 'InfDeclaracaoPrestacaoServico')) {
    layout = ABRASF_LAYOUT;
    data = parseAbrasf(doc, warnings);
  } else {
    throw new Error(
      'XML não reconhecido. Envie o XML da NFS-e emitida pelo portal do Emissor '
      + 'Nacional ou pela prefeitura (padrão ABRASF).',
    );
  }

  if (data.service.issRate === null) {
    warnings.push(
      'A alíquota de ISS não consta no XML (acontece em nota do Simples Nacional '
      + 'sem ISS retido) — informe o percentual do seu município.',
    );
  }

  if (data.service.description) {
    warnings.push(
      'A descrição do serviço foi copiada da nota enviada — ajuste para o texto '
      + 'padrão que você quer usar nas próximas.',
    );
  }

  const derived = ['pisRate', 'cofinsRate', 'irRate', 'csllRate', 'inssRate']
    .some(key => data.service[key] > 0);
  if (derived) {
    warnings.push(
      'PIS, COFINS, IR, CSLL e INSS constam no XML apenas em valor — os '
      + 'percentuais foram calculados sobre o valor do serviço. Confira.',
    );
  }

  return { layout, data, warnings };
}

/**
 * Lê o arquivo respeitando a declaração de encoding: o XML do IPM sai em
 * ISO-8859-1 e, lido como UTF-8, quebra os acentos.
 *
 * @param {File} file
 * @returns {Promise<string>}
 */
export function readXmlFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.onload = () => {
      const buffer = reader.result;
      const head = new TextDecoder('utf-8')
        .decode(new Uint8Array(buffer, 0, Math.min(200, buffer.byteLength)));
      const match = head.match(/encoding=["']([^"']+)["']/i);
      const encoding = match ? match[1] : 'utf-8';

      try {
        resolve(new TextDecoder(encoding).decode(buffer));
      } catch (e) {
        resolve(new TextDecoder('utf-8').decode(buffer));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}
